import { Checkbox, FormControl, FormControlLabel } from '@mui/material';
import { FormikProps } from 'formik';
import React from 'react';
import { v4 } from 'uuid';
import * as Yup from 'yup';

import {
  CHECKBOX_CHECKED_VALUE,
  CHECKBOX_UNCHECKED_VALUE,
  CONTRACT_CHECKBOX_LABEL_MAX_LENGTH,
  FormBodyAPI,
} from 'src/constants';
import BaseTypography from 'src/legacy/components/Text/BaseTypography';
import { BaseTextField } from 'src/legacy/components/TextField';

interface FormValues {
  label: string;
  // 'checked' | 'unchecked' — stored directly as the field value.
  value: string;
}

// Form for the internal user's own ("My") checkbox: an optional label plus the
// checked state, which the IU sets directly. The label is optional because the box
// itself reads as a checkbox without copy.
export const useAddCheckboxForm = () => {
  const validationScheme = Yup.object().shape({
    label: Yup.string()
      .trim()
      .max(
        CONTRACT_CHECKBOX_LABEL_MAX_LENGTH,
        `Maximum ${CONTRACT_CHECKBOX_LABEL_MAX_LENGTH} characters`,
      ),
  });

  const getSubmitValues = () => Promise.resolve({ key: v4() });

  const FormRenderer: React.FC<FormBodyAPI<FormValues>> = (props) => {
    const { touched, errors, values, setFieldValue, handleChange, handleBlur } =
      props as FormikProps<FormValues>;

    const isChecked = values.value === CHECKBOX_CHECKED_VALUE;

    return (
      <div>
        <FormControl fullWidth>
          <BaseTypography fontType="13Medium">Label</BaseTypography>
          <BaseTextField
            name="label"
            onChange={handleChange}
            onBlur={handleBlur}
            helperText={touched && errors ? touched.label && errors.label : ''}
            variant="outlined"
            error={Boolean(touched.label && errors.label)}
            inputProps={{
              'data-testid': 'checkbox-label-input',
              maxLength: CONTRACT_CHECKBOX_LABEL_MAX_LENGTH + 1,
            }}
            autoFocus
          />
        </FormControl>
        <div className="mt-4">
          <FormControlLabel
            control={
              <Checkbox
                checked={isChecked}
                name="value"
                onChange={() =>
                  setFieldValue(
                    'value',
                    isChecked
                      ? CHECKBOX_UNCHECKED_VALUE
                      : CHECKBOX_CHECKED_VALUE,
                  )
                }
                color="primary"
                data-testid="my-checkbox-checked"
              />
            }
            label="Checked"
            id="my-checkbox-checked-control"
          />
        </div>
      </div>
    );
  };

  return { validationScheme, getSubmitValues, FormRenderer };
};
